<div class="advanced-bundle-edit-container">
  <!-- Tabs Section -->
  <mat-tab-group class="bundle-tabs" [selectedIndex]="selectedTabIndex">
    <!-- Tab 1: Bundle Information -->
    <mat-tab [label]="bundleInformationTabLabel">
      <div class="tab-content">
        <div class="bundle-info-card">
          <!-- Bundle Information Grid: 3 columns x 2 rows -->
          <div class="bundle-info-grid" [formGroup]="bundleForm">
            <!-- Row 1, Column 1: Bundle Name -->
            <mat-form-field appearance="outline" class="bundle-field">
              <mat-label>Bundle Name</mat-label>
              <input matInput formControlName="name" placeholder="Enter bundle name" required />
              <mat-error *ngIf="bundleForm.controls.name.errors?.required">
                Bundle name is required.
              </mat-error>
              <mat-error *ngIf="bundleForm.controls.name.errors?.maxlength">
                Bundle name cannot exceed 100 characters.
              </mat-error>
            </mat-form-field>

            <!-- Row 1, Column 2: Instrument Type (Read-only) -->
            <mat-form-field appearance="outline" class="bundle-field">
              <mat-label>Instrument Type</mat-label>
              <mat-select [value]="selectedDeviceType" disabled>
                <mat-option *ngFor="let device of devices" [value]="device.id">
                  {{ device.value }}
                </mat-option>
              </mat-select>
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>

            <!-- Row 1-2, Column 3: Description (textarea spanning 2 rows) -->
            <mat-form-field appearance="outline" class="bundle-field description-field">
              <mat-label>Description</mat-label>
              <textarea
                matInput
                formControlName="description"
                placeholder="Enter bundle description"
                rows="4"
                [style.resize]="'none'"
              ></textarea>
              <mat-error *ngIf="bundleForm.controls.description.errors?.maxlength">
                Description cannot exceed 500 characters.
              </mat-error>
            </mat-form-field>

            <!-- Row 2, Column 1: Date Created (Read-only) -->
            <mat-form-field appearance="outline" class="bundle-field">
              <mat-label>Date Created</mat-label>
              <input matInput [value]="bundleDateCreated | date: 'shortDate'" readonly disabled />
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>

            <!-- Row 2, Column 2: Created By (Read-only) -->
            <mat-form-field appearance="outline" class="bundle-field">
              <mat-label>Created By</mat-label>
              <input matInput [value]="bundleCreatedByName" readonly disabled />
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>
          </div>
        </div>

        <!-- Bundle Actions -->
        <div class="bundle-actions">
          <button
            mat-raised-button
            color="primary"
            (click)="onSaveBundle()"
            [disabled]="bundleForm.invalid || isLoading"
          >
            <mat-icon>save</mat-icon>
            Save Changes
          </button>
          <button mat-stroked-button (click)="onCancel()" [disabled]="isLoading">
            <mat-icon>cancel</mat-icon>
            Cancel
          </button>
        </div>
      </div>
    </mat-tab>

    <!-- Tab 2: Filters -->
    <mat-tab label="Search for Test Results">
      <div class="tab-content tab-content-with-filters">
        <!-- Filters Section -->
        <mat-card class="filters-card">
          <mat-card-content class="filters-content">
            <!-- Sofia2 Filters Form -->
            <div
              class="filters-grid"
              [formGroup]="sofia2FiltersForm"
              *ngIf="selectedDeviceType === 1"
            >
              <!-- Grid: 6 columns x 3 rows -->
              <!-- Row 1: Test Result ID, Assay ID, Sample ID (textarea), Cassette Lot (textarea), Instrument Serial Number (textarea), Cassette Serial Number (textarea) -->
              <div class="filter-row">
                <!-- Column 1: Test Result ID -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Test Result ID</mat-label>
                  <input
                    matInput
                    formControlName="testResultId"
                    placeholder="Enter test result ID"
                    maxlength="500"
                  />
                  <mat-error *ngIf="sofia2FiltersForm.get('testResultId')?.hasError('maxlength')">
                    Test Result ID cannot exceed 500 characters
                  </mat-error>
                </mat-form-field>

                <!-- Column 2: Assay ID -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Assay ID</mat-label>
                  <input
                    matInput
                    type="text"
                    formControlName="assayId"
                    placeholder="Enter assay ID"
                    maxlength="9"
                    (keypress)="onAssayIdKeyPress($event)"
                    (input)="onAssayIdInput($event)"
                  />
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('pattern')">
                    Assay ID must be a whole number
                  </mat-error>
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('maxlength')">
                    Assay ID cannot exceed 9 digits
                  </mat-error>
                </mat-form-field>

                <!-- Column 3: Sample ID (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>Sample ID</mat-label>
                  <textarea
                    matInput
                    formControlName="sampleId"
                    placeholder="Enter sample IDs separated by comma, space, tab, or newline"
                    rows="4"
                    maxlength="2000"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                </mat-form-field>

                <!-- Column 4: Cassette Lot (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>{{ cassetteLotLabel }}</mat-label>
                  <textarea
                    matInput
                    formControlName="cassetteLot"
                    [placeholder]="cassetteLotPlaceholder"
                    rows="4"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                </mat-form-field>

                <!-- Column 5: Instrument Serial Number (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>Instrument Serial Number</mat-label>
                  <textarea
                    matInput
                    formControlName="instrumentSerialNumber"
                    placeholder="Enter serial numbers separated by comma, space, tab, or newline"
                    rows="4"
                    maxlength="2000"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                  <mat-error
                    *ngIf="sofia2FiltersForm.get('instrumentSerialNumber')?.hasError('maxlength')"
                  >
                    Instrument Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>

                <!-- Column 6: Cassette Serial Number (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>{{ cassetteSerialLabel }}</mat-label>
                  <textarea
                    matInput
                    formControlName="cassetteSerialNumber"
                    [placeholder]="cassetteSerialPlaceholder"
                    rows="4"
                    maxlength="2000"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                  <mat-error
                    *ngIf="sofia2FiltersForm.get('cassetteSerialNumber')?.hasError('maxlength')"
                  >
                    Cassette Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>
              </div>

              <!-- Row 2: Start Date, End Date, Columns 3-6 are already occupied by textareas above -->
              <div class="filter-row">
                <!-- Column 1: Start Date -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Start Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="sofia2StartDatePicker"
                    formControlName="startDate"
                    placeholder="Select start date"
                  />
                  <mat-datepicker-toggle
                    matSuffix
                    [for]="sofia2StartDatePicker"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #sofia2StartDatePicker></mat-datepicker>
                </mat-form-field>

                <!-- Column 2: End Date -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>End Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="sofia2EndDatePicker"
                    formControlName="endDate"
                    placeholder="Select end date"
                  />
                  <mat-datepicker-toggle
                    matSuffix
                    [for]="sofia2EndDatePicker"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #sofia2EndDatePicker></mat-datepicker>
                </mat-form-field>

                <!-- Columns 3-4: Empty (occupied by textareas from row 1) -->
                <div class="filter-field filter-field-textarea"></div>
                <div class="filter-field filter-field-textarea"></div>

                <!-- Columns 5-6: Empty (occupied by textareas from row 1) -->
                <div class="filter-field filter-field-textarea"></div>
                <div class="filter-field filter-field-textarea"></div>
              </div>

              <!-- Row 3: Assay Name, Filter Actions (Columns 5-6) -->
              <div class="filter-row">
                <!-- Column 1: Assay Name -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Assay Name</mat-label>
                  <input
                    matInput
                    formControlName="assayName"
                    placeholder="Enter assay name"
                    maxlength="200"
                  />
                  <mat-error *ngIf="sofia2FiltersForm.get('assayName')?.hasError('maxlength')">
                    Assay Name cannot exceed 200 characters
                  </mat-error>
                </mat-form-field>

                <!-- Column 2: Empty space -->
                <div class="filter-field"></div>

                <!-- Columns 3-4: Empty (occupied by textareas from row 1) -->
                <div class="filter-field filter-field-textarea"></div>
                <div class="filter-field filter-field-textarea"></div>

                <!-- Columns 5-6: Filter Actions (right-aligned) -->
                <div class="filter-field filter-field-textarea filter-actions-right">
                  <button
                    mat-raised-button
                    color="primary"
                    (click)="applyFilters()"
                    [disabled]="isLoading"
                  >
                    <mat-icon>search</mat-icon>
                    Apply Filters
                  </button>
                  <button mat-stroked-button (click)="clearFilters()" [disabled]="isLoading">
                    <mat-icon>clear</mat-icon>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            <!-- Savanna Filters Form -->
            <div
              class="filters-grid"
              [formGroup]="savannaFiltersForm"
              *ngIf="selectedDeviceType === 2"
            >
              <!-- Grid: 6 columns x 3 rows -->
              <!-- Row 1: Test Result ID, Assay ID, Sample ID (textarea), Cartridge Lot (textarea), Instrument Serial Number (textarea), Cartridge Serial Number (textarea) -->
              <div class="filter-row">
                <!-- Column 1: Test Result ID -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Test Result ID</mat-label>
                  <input
                    matInput
                    formControlName="testResultId"
                    placeholder="Enter test result ID"
                    maxlength="500"
                  />
                  <mat-error *ngIf="savannaFiltersForm.get('testResultId')?.hasError('maxlength')">
                    Test Result ID cannot exceed 500 characters
                  </mat-error>
                </mat-form-field>

                <!-- Column 2: Assay ID -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Assay ID</mat-label>
                  <input
                    matInput
                    type="text"
                    formControlName="assayId"
                    placeholder="Enter assay ID"
                    maxlength="9"
                    (keypress)="onAssayIdKeyPress($event)"
                    (input)="onAssayIdInput($event)"
                  />
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('pattern')">
                    Assay ID must be a whole number
                  </mat-error>
                  <mat-error *ngIf="savannaFiltersForm.get('assayId')?.hasError('maxlength')">
                    Assay ID cannot exceed 9 digits
                  </mat-error>
                </mat-form-field>

                <!-- Column 3: Sample ID (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>Sample ID</mat-label>
                  <textarea
                    matInput
                    formControlName="sampleId"
                    placeholder="Enter sample IDs separated by comma, space, tab, or newline"
                    rows="4"
                    maxlength="2000"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                </mat-form-field>

                <!-- Column 4: Cartridge Lot (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>{{ cassetteLotLabel }}</mat-label>
                  <textarea
                    matInput
                    formControlName="cartridgeLot"
                    [placeholder]="cassetteLotPlaceholder"
                    rows="4"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                </mat-form-field>

                <!-- Column 5: Instrument Serial Number (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>Instrument Serial Number</mat-label>
                  <textarea
                    matInput
                    formControlName="instrumentSerialNumber"
                    placeholder="Enter serial numbers separated by comma, space, tab, or newline"
                    rows="4"
                    maxlength="2000"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                  <mat-error
                    *ngIf="savannaFiltersForm.get('instrumentSerialNumber')?.hasError('maxlength')"
                  >
                    Instrument Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>

                <!-- Column 6: Cassette/Cartridge Serial Number (textarea, spans 2 rows) -->
                <mat-form-field appearance="outline" class="filter-field filter-field-textarea">
                  <mat-label>{{ cassetteSerialLabel }}</mat-label>
                  <textarea
                    matInput
                    formControlName="cartridgeSerialNumber"
                    [placeholder]="cassetteSerialPlaceholder"
                    rows="4"
                    maxlength="2000"
                  ></textarea>
                  <mat-hint
                    >Enter multiple values separated by comma, space, tab, or newline</mat-hint
                  >
                  <mat-error
                    *ngIf="savannaFiltersForm.get('cartridgeSerialNumber')?.hasError('maxlength')"
                  >
                    Cartridge Serial Number cannot exceed 2000 characters
                  </mat-error>
                </mat-form-field>
              </div>

              <!-- Row 2: Start Date, End Date, Columns 3-6 are already occupied by textareas above -->
              <div class="filter-row">
                <!-- Column 1: Start Date -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Start Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="savannaStartDatePicker"
                    formControlName="startDate"
                    placeholder="Select start date"
                  />
                  <mat-datepicker-toggle
                    matSuffix
                    [for]="savannaStartDatePicker"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #savannaStartDatePicker></mat-datepicker>
                </mat-form-field>

                <!-- Column 2: End Date -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>End Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="savannaEndDatePicker"
                    formControlName="endDate"
                    placeholder="Select end date"
                  />
                  <mat-datepicker-toggle
                    matSuffix
                    [for]="savannaEndDatePicker"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #savannaEndDatePicker></mat-datepicker>
                </mat-form-field>

                <!-- Columns 3-4: Empty (occupied by textareas from row 1) -->
                <div class="filter-field filter-field-textarea"></div>
                <div class="filter-field filter-field-textarea"></div>

                <!-- Columns 5-6: Empty (occupied by textareas from row 1) -->
                <div class="filter-field filter-field-textarea"></div>
                <div class="filter-field filter-field-textarea"></div>
              </div>

              <!-- Row 3: Assay Name, Filter Actions (Columns 5-6) -->
              <div class="filter-row">
                <!-- Column 1: Assay Name -->
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-label>Assay Name</mat-label>
                  <input matInput formControlName="assayName" placeholder="Enter assay name" />
                </mat-form-field>

                <!-- Column 2: Empty space -->
                <div class="filter-field"></div>

                <!-- Columns 3-4: Empty (occupied by textareas from row 1) -->
                <div class="filter-field filter-field-textarea"></div>
                <div class="filter-field filter-field-textarea"></div>

                <!-- Columns 5-6: Filter Actions (right-aligned) -->
                <div class="filter-field filter-field-textarea filter-actions-right">
                  <button
                    mat-raised-button
                    color="primary"
                    (click)="applyFilters()"
                    [disabled]="isLoading"
                  >
                    <mat-icon>search</mat-icon>
                    Apply Filters
                  </button>
                  <button mat-stroked-button (click)="clearFilters()" [disabled]="isLoading">
                    <mat-icon>clear</mat-icon>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Results Section -->
        <div class="results-section">
          <div *ngIf="isQueryingResults" class="loading-container">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            <p>Searching for test results...</p>
          </div>

          <!-- Sofia2 Results Table (visible when deviceType is 1) -->
          <div
            *ngIf="!isQueryingResults && selectedDeviceType === 1"
            class="device-results-container sofia2-results"
          >
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field">
                  <mat-label>Search test results</mat-label>
                  <input
                    matInput
                    [(ngModel)]="searchQuery"
                    (ngModelChange)="onSearchChange()"
                    placeholder="Type to filter..."
                  />
                  <button
                    *ngIf="searchQuery"
                    matSuffix
                    mat-icon-button
                    aria-label="Clear"
                    (click)="searchQuery = ''; onSearchChange()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <mat-slide-toggle
                  [checked]="showAlreadyAddedTests"
                  (change)="onToggleShowAlreadyAddedTests($event.checked)"
                  color="primary"
                  class="toggle-filter"
                >
                  Show already added
                </mat-slide-toggle>
                <button
                  mat-raised-button
                  color="primary"
                  (click)="addSelectedTestsToTable()"
                  [disabled]="isAddTestButtonDisabled()"
                >
                  <mat-icon>add</mat-icon>
                  Add Test<span *ngIf="getSelectedTestsCount() > 0">
                    ({{ getSelectedTestsCount() }})</span
                  >
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  (click)="clearSorting()"
                  aria-label="Clear sorting"
                >
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="results-table-wrapper">
                <table class="results-table sofia2-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          [checked]="areAllCurrentFilteredSelected()"
                          (change)="toggleSelectAllCurrentFiltered()"
                        />
                      </th>
                      <th
                        *ngFor="let column of sofia2Columns"
                        [class.sortable]="column.sortable"
                        [attr.aria-sort]="
                          column.sortable && isColumnSorted(column.key)
                            ? getColumnSortDirection(column.key) === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        "
                        (click)="column.sortable && onSort(column.key)"
                      >
                        {{ getColumnLabel(column) }}
                        <mat-icon
                          *ngIf="column.sortable"
                          class="sort-indicator"
                          [class.active]="isColumnSorted(column.key)"
                          >{{
                            isColumnSorted(column.key)
                              ? getColumnSortDirection(column.key) === 'asc'
                                ? 'arrow_upward'
                                : 'arrow_downward'
                              : 'unfold_more'
                          }}</mat-icon
                        >
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredSofia2Results.length === 0" class="no-results-row">
                      <td colspan="12" class="no-results-cell">
                        <p>No Sofia 2 test results found matching the specified filters.</p>
                        <p *ngIf="!showResults" class="no-filters-applied">
                          Apply filters above to search for test results.
                        </p>
                      </td>
                    </tr>
                    <tr
                      *ngFor="let result of paginatedSofia2Results"
                      [class.already-added]="isTestAlreadyAdded(result.testResultId)"
                    >
                      <td>
                        <input
                          *ngIf="!isTestAlreadyAdded(result.testResultId)"
                          type="checkbox"
                          [checked]="isSelected(result.testResultId)"
                          (change)="toggleRowSelection(result.testResultId)"
                        />
                      </td>
                      <td
                        *ngFor="let column of sofia2Columns"
                        [matTooltip]="
                          column.key === 'dateTimeUtc'
                            ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                            : column.key === 'dateTime'
                              ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                              : column.key === 'result'
                                ? (result.result || '-')
                                : (getColumnValue(result, column.key) || '-')
                        "
                      >
                        <ng-container [ngSwitch]="column.key">
                          <span *ngSwitchCase="'dateTimeUtc'">
                            {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                          </span>
                          <span *ngSwitchCase="'dateTime'">
                            {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                          </span>
                          <span *ngSwitchCase="'result'">
                            <mat-chip
                              *ngIf="result.result && result.result !== '-'"
                              class="result-chip"
                              [class.result-positive]="result.result.toLowerCase() === 'positive'"
                              [class.result-negative]="result.result.toLowerCase() === 'negative'"
                            >
                              {{ result.result }}
                            </mat-chip>
                            <span *ngIf="!result.result || result.result === '-'">-</span>
                          </span>
                          <span *ngSwitchDefault>
                            {{ getColumnValue(result, column.key) || '-' }}
                          </span>
                        </ng-container>
                      </td>
                      <td>
                        <div class="action-buttons-container">
                          <button
                            mat-icon-button
                            color="primary"
                            size="small"
                            (click)="addSingleTestToTable(result)"
                            [disabled]="isTestAlreadyAdded(result.testResultId)"
                            aria-label="Add test"
                          >
                            <mat-icon>{{
                              isTestAlreadyAdded(result.testResultId)
                                ? 'check_circle'
                                : 'add_circle'
                            }}</mat-icon>
                          </button>
                          <button
                            *ngIf="isTestAlreadyAdded(result.testResultId)"
                            mat-icon-button
                            color="warn"
                            size="small"
                            (click)="excludeTestFromSelected(result.testResultId)"
                            aria-label="Remove from selected tests"
                          >
                            <mat-icon>delete</mat-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <mat-paginator
                *ngIf="filteredSofia2Results.length > 0"
                [length]="filteredSofia2Results.length"
                [pageSize]="pageSize"
                [pageIndex]="pageIndex"
                [pageSizeOptions]="pageSizeOptions"
                [showFirstLastButtons]="true"
                (page)="onPageChange($event)"
                class="results-paginator"
              ></mat-paginator>
            </div>
          </div>

          <!-- Savanna Results Table (visible when deviceType is 2) -->
          <div
            *ngIf="!isQueryingResults && selectedDeviceType === 2"
            class="device-results-container savanna-results"
          >
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field">
                  <mat-label>Search test results</mat-label>
                  <input
                    matInput
                    [(ngModel)]="searchQuery"
                    (ngModelChange)="onSearchChange()"
                    placeholder="Type to filter..."
                  />
                  <button
                    *ngIf="searchQuery"
                    matSuffix
                    mat-icon-button
                    aria-label="Clear"
                    (click)="searchQuery = ''; onSearchChange()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <mat-slide-toggle
                  [checked]="showAlreadyAddedTests"
                  (change)="onToggleShowAlreadyAddedTests($event.checked)"
                  color="primary"
                  class="toggle-filter"
                >
                  Show already added
                </mat-slide-toggle>
                <button
                  mat-raised-button
                  color="primary"
                  (click)="addSelectedTestsToTable()"
                  [disabled]="isAddTestButtonDisabled()"
                >
                  <mat-icon>add</mat-icon>
                  Add Test<span *ngIf="getSelectedTestsCount() > 0">
                    ({{ getSelectedTestsCount() }})</span
                  >
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  (click)="clearSorting()"
                  aria-label="Clear sorting"
                >
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="results-table-wrapper">
                <table class="results-table savanna-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          [checked]="areAllCurrentFilteredSelected()"
                          (change)="toggleSelectAllCurrentFiltered()"
                        />
                      </th>
                      <th
                        *ngFor="let column of savannaColumns"
                        [class.sortable]="column.sortable"
                        [attr.aria-sort]="
                          column.sortable && isColumnSorted(column.key)
                            ? getColumnSortDirection(column.key) === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        "
                        (click)="column.sortable && onSort(column.key)"
                      >
                        {{ getColumnLabel(column) }}
                        <mat-icon
                          *ngIf="column.sortable"
                          class="sort-indicator"
                          [class.active]="isColumnSorted(column.key)"
                          >{{
                            isColumnSorted(column.key)
                              ? getColumnSortDirection(column.key) === 'asc'
                                ? 'arrow_upward'
                                : 'arrow_downward'
                              : 'unfold_more'
                          }}</mat-icon
                        >
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredSavannaResults.length === 0" class="no-results-row">
                      <td colspan="12" class="no-results-cell">
                        <p>No Savanna test results found matching the specified filters.</p>
                        <p *ngIf="!showResults" class="no-filters-applied">
                          Apply filters above to search for test results.
                        </p>
                      </td>
                    </tr>
                    <tr
                      *ngFor="let result of paginatedSavannaResults"
                      [class.already-added]="isTestAlreadyAdded(result.testResultId)"
                    >
                      <td>
                        <input
                          *ngIf="!isTestAlreadyAdded(result.testResultId)"
                          type="checkbox"
                          [checked]="isSelected(result.testResultId)"
                          (change)="toggleRowSelection(result.testResultId)"
                        />
                      </td>
                      <td
                        *ngFor="let column of savannaColumns"
                        [matTooltip]="
                          column.key === 'dateTimeUtc'
                            ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                            : column.key === 'dateTime'
                              ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                              : column.key === 'result'
                                ? (result.result || '-')
                                : (getColumnValue(result, column.key) || '-')
                        "
                      >
                        <ng-container [ngSwitch]="column.key">
                          <span *ngSwitchCase="'dateTimeUtc'">
                            {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                          </span>
                          <span *ngSwitchCase="'dateTime'">
                            {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                          </span>
                          <span *ngSwitchCase="'result'">
                            <mat-chip
                              *ngIf="result.result && result.result !== '-'"
                              class="result-chip"
                              [class.result-positive]="result.result.toLowerCase() === 'positive'"
                              [class.result-negative]="result.result.toLowerCase() === 'negative'"
                            >
                              {{ result.result }}
                            </mat-chip>
                            <span *ngIf="!result.result || result.result === '-'">-</span>
                          </span>
                          <span *ngSwitchDefault>
                            {{ getColumnValue(result, column.key) || '-' }}
                          </span>
                        </ng-container>
                      </td>
                      <td>
                        <div class="action-buttons-container">
                          <button
                            mat-icon-button
                            color="primary"
                            size="small"
                            (click)="addSingleTestToTable(result)"
                            [disabled]="isTestAlreadyAdded(result.testResultId)"
                            aria-label="Add test"
                          >
                            <mat-icon>{{
                              isTestAlreadyAdded(result.testResultId)
                                ? 'check_circle'
                                : 'add_circle'
                            }}</mat-icon>
                          </button>
                          <button
                            *ngIf="isTestAlreadyAdded(result.testResultId)"
                            mat-icon-button
                            color="warn"
                            size="small"
                            (click)="excludeTestFromSelected(result.testResultId)"
                            aria-label="Remove from selected tests"
                          >
                            <mat-icon>delete</mat-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <mat-paginator
                *ngIf="filteredSavannaResults.length > 0"
                [length]="filteredSavannaResults.length"
                [pageSize]="pageSize"
                [pageIndex]="pageIndex"
                [pageSizeOptions]="pageSizeOptions"
                [showFirstLastButtons]="true"
                (page)="onPageChange($event)"
                class="results-paginator"
              ></mat-paginator>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>

    <!-- Tab 3: Selected Tests -->
    <mat-tab [label]="selectedTestsTabLabel">
      <div class="tab-content">
        <div class="selected-tests-section">
          <!-- Sofia2 Selected Tests Table (visible when deviceType is 1) -->
          <div *ngIf="selectedDeviceType === 1" class="device-results-container sofia2-results">
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field">
                  <mat-label>Search selected tests</mat-label>
                  <input
                    matInput
                    [(ngModel)]="selectedTestsSearchQuery"
                    (ngModelChange)="onSelectedTestsSearchChange()"
                    placeholder="Type to filter..."
                  />
                  <button
                    *ngIf="selectedTestsSearchQuery"
                    matSuffix
                    mat-icon-button
                    aria-label="Clear"
                    (click)="selectedTestsSearchQuery = ''; onSelectedTestsSearchChange()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <button
                  mat-raised-button
                  color="warn"
                  (click)="deleteSelectedTestsFromSelected()"
                  [disabled]="getSelectedTestsSelectionCount() === 0"
                >
                  <mat-icon>delete</mat-icon>
                  Delete Selected ({{ getSelectedTestsSelectionCount() }})
                </button>
                <button
                  mat-raised-button
                  color="primary"
                  (click)="onSaveSelectedTests()"
                  [disabled]="isLoading"
                >
                  <mat-icon *ngIf="!isLoading">save</mat-icon>
                  <mat-icon *ngIf="isLoading" class="spinning">refresh</mat-icon>
                  {{
                    isLoading
                      ? 'Saving...'
                      : sofia2SelectedTests.length === 0
                        ? 'Save (Clear All)'
                        : `Save ${sofia2SelectedTests.length} Test Result(s)`
                  }}
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  (click)="clearSelectedTestsSorting()"
                  aria-label="Clear sorting"
                >
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="results-table-wrapper">
                <table class="results-table sofia2-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          [checked]="areAllCurrentSelectedTestsSelected()"
                          (change)="toggleSelectAllSelectedTests()"
                        />
                      </th>
                      <th
                        *ngFor="let column of sofia2Columns"
                        [class.sortable]="column.sortable"
                        [attr.aria-sort]="
                          column.sortable && isSelectedTestsColumnSorted(column.key)
                            ? getSelectedTestsColumnSortDirection(column.key) === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        "
                        (click)="column.sortable && onSelectedTestsSort(column.key)"
                      >
                        {{ getColumnLabel(column) }}
                        <mat-icon
                          *ngIf="column.sortable"
                          class="sort-indicator"
                          [class.active]="isSelectedTestsColumnSorted(column.key)"
                          >{{
                            isSelectedTestsColumnSorted(column.key)
                              ? getSelectedTestsColumnSortDirection(column.key) === 'asc'
                                ? 'arrow_upward'
                                : 'arrow_downward'
                              : 'unfold_more'
                          }}</mat-icon
                        >
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredSelectedSofia2Tests.length === 0" class="no-results-row">
                      <td colspan="13" class="no-results-cell">
                        <p>
                          No selected tests. Use the "Add Test" button in the Filters tab to add
                          tests.
                        </p>
                      </td>
                    </tr>
                    <tr *ngFor="let result of paginatedSelectedSofia2Tests">
                      <td>
                        <input
                          type="checkbox"
                          [checked]="isSelectedTestSelected(result.testResultId)"
                          (change)="toggleSelectedTestSelection(result.testResultId)"
                        />
                      </td>
                      <td
                        *ngFor="let column of sofia2Columns"
                        [matTooltip]="
                          column.key === 'dateTimeUtc'
                            ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                            : column.key === 'dateTime'
                              ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                              : column.key === 'result'
                                ? (result.result || '-')
                                : (getColumnValue(result, column.key) || '-')
                        "
                      >
                        <ng-container [ngSwitch]="column.key">
                          <span *ngSwitchCase="'dateTimeUtc'">
                            {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                          </span>
                          <span *ngSwitchCase="'dateTime'">
                            {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                          </span>
                          <span *ngSwitchCase="'result'">
                            <mat-chip
                              *ngIf="result.result && result.result !== '-'"
                              class="result-chip"
                              [class.result-positive]="result.result.toLowerCase() === 'positive'"
                              [class.result-negative]="result.result.toLowerCase() === 'negative'"
                            >
                              {{ result.result }}
                            </mat-chip>
                            <span *ngIf="!result.result || result.result === '-'">-</span>
                          </span>
                          <span *ngSwitchDefault>
                            {{ getColumnValue(result, column.key) || '-' }}
                          </span>
                        </ng-container>
                      </td>
                      <td>
                        <button
                          mat-icon-button
                          color="warn"
                          size="small"
                          (click)="excludeTestFromSelected(result.testResultId)"
                          aria-label="Exclude test"
                        >
                          <mat-icon>delete</mat-icon>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <mat-paginator
                *ngIf="filteredSelectedSofia2Tests.length > 0"
                [length]="filteredSelectedSofia2Tests.length"
                [pageSize]="selectedTestsPageSize"
                [pageIndex]="selectedTestsPageIndex"
                [pageSizeOptions]="selectedTestsPageSizeOptions"
                [showFirstLastButtons]="true"
                (page)="onSelectedTestsPageChange($event)"
                class="results-paginator"
              ></mat-paginator>
            </div>
          </div>

          <!-- Savanna Selected Tests Table (visible when deviceType is 2) -->
          <div *ngIf="selectedDeviceType === 2" class="device-results-container savanna-results">
            <div class="results-table-container">
              <div class="results-toolbar">
                <mat-form-field appearance="outline" class="search-field">
                  <mat-label>Search selected tests</mat-label>
                  <input
                    matInput
                    [(ngModel)]="selectedTestsSearchQuery"
                    (ngModelChange)="onSelectedTestsSearchChange()"
                    placeholder="Type to filter..."
                  />
                  <button
                    *ngIf="selectedTestsSearchQuery"
                    matSuffix
                    mat-icon-button
                    aria-label="Clear"
                    (click)="selectedTestsSearchQuery = ''; onSelectedTestsSearchChange()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </mat-form-field>
                <span class="spacer"></span>
                <button
                  mat-raised-button
                  color="warn"
                  (click)="deleteSelectedTestsFromSelected()"
                  [disabled]="getSelectedTestsSelectionCount() === 0"
                >
                  <mat-icon>delete</mat-icon>
                  Delete Selected ({{ getSelectedTestsSelectionCount() }})
                </button>
                <button
                  mat-raised-button
                  color="primary"
                  (click)="onSaveSelectedTests()"
                  [disabled]="isLoading"
                >
                  <mat-icon *ngIf="!isLoading">save</mat-icon>
                  <mat-icon *ngIf="isLoading" class="spinning">refresh</mat-icon>
                  {{
                    isLoading
                      ? 'Saving...'
                      : savannaSelectedTests.length === 0
                        ? 'Save (Clear All)'
                        : `Save ${savannaSelectedTests.length} Test Result(s)`
                  }}
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  (click)="clearSelectedTestsSorting()"
                  aria-label="Clear sorting"
                >
                  <mat-icon>sort</mat-icon>
                  Clear sorting
                </button>
              </div>
              <div class="results-table-wrapper">
                <table class="results-table savanna-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          [checked]="areAllCurrentSelectedTestsSelected()"
                          (change)="toggleSelectAllSelectedTests()"
                        />
                      </th>
                      <th
                        *ngFor="let column of savannaColumns"
                        [class.sortable]="column.sortable"
                        [attr.aria-sort]="
                          column.sortable && isSelectedTestsColumnSorted(column.key)
                            ? getSelectedTestsColumnSortDirection(column.key) === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        "
                        (click)="column.sortable && onSelectedTestsSort(column.key)"
                      >
                        {{ getColumnLabel(column) }}
                        <mat-icon
                          *ngIf="column.sortable"
                          class="sort-indicator"
                          [class.active]="isSelectedTestsColumnSorted(column.key)"
                          >{{
                            isSelectedTestsColumnSorted(column.key)
                              ? getSelectedTestsColumnSortDirection(column.key) === 'asc'
                                ? 'arrow_upward'
                                : 'arrow_downward'
                              : 'unfold_more'
                          }}</mat-icon
                        >
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredSelectedSavannaTests.length === 0" class="no-results-row">
                      <td colspan="13" class="no-results-cell">
                        <p>
                          No selected tests. Use the "Add Test" button in the Filters tab to add
                          tests.
                        </p>
                      </td>
                    </tr>
                    <tr *ngFor="let result of paginatedSelectedSavannaTests">
                      <td>
                        <input
                          type="checkbox"
                          [checked]="isSelectedTestSelected(result.testResultId)"
                          (change)="toggleSelectedTestSelection(result.testResultId)"
                        />
                      </td>
                      <td
                        *ngFor="let column of savannaColumns"
                        [matTooltip]="
                          column.key === 'dateTimeUtc'
                            ? (result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-')
                            : column.key === 'dateTime'
                              ? (result.dateTime ? (result.dateTime | date: 'short') : '-')
                              : column.key === 'result'
                                ? (result.result || '-')
                                : (getColumnValue(result, column.key) || '-')
                        "
                      >
                        <ng-container [ngSwitch]="column.key">
                          <span *ngSwitchCase="'dateTimeUtc'">
                            {{ result.dateTimeUtc ? formatUtcDate(result.dateTimeUtc) : '-' }}
                          </span>
                          <span *ngSwitchCase="'dateTime'">
                            {{ result.dateTime ? (result.dateTime | date: 'short') : '-' }}
                          </span>
                          <span *ngSwitchCase="'result'">
                            <mat-chip
                              *ngIf="result.result && result.result !== '-'"
                              class="result-chip"
                              [class.result-positive]="result.result.toLowerCase() === 'positive'"
                              [class.result-negative]="result.result.toLowerCase() === 'negative'"
                            >
                              {{ result.result }}
                            </mat-chip>
                            <span *ngIf="!result.result || result.result === '-'">-</span>
                          </span>
                          <span *ngSwitchDefault>
                            {{ getColumnValue(result, column.key) || '-' }}
                          </span>
                        </ng-container>
                      </td>
                      <td>
                        <button
                          mat-icon-button
                          color="warn"
                          size="small"
                          (click)="excludeTestFromSelected(result.testResultId)"
                          aria-label="Exclude test"
                        >
                          <mat-icon>delete</mat-icon>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <mat-paginator
                *ngIf="filteredSelectedSavannaTests.length > 0"
                [length]="filteredSelectedSavannaTests.length"
                [pageSize]="selectedTestsPageSize"
                [pageIndex]="selectedTestsPageIndex"
                [pageSizeOptions]="selectedTestsPageSizeOptions"
                [showFirstLastButtons]="true"
                (page)="onSelectedTestsPageChange($event)"
                class="results-paginator"
              ></mat-paginator>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
